import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import "@/App.css";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { CopoBuilder } from "@/components/CopoBuilder";
import { AISuggester } from "@/components/AISuggester";
import { Roleta } from "@/components/Roleta";
import { Promos } from "@/components/Promos";
import { ExtrasSection } from "@/components/ExtrasSection";
import { StoreInfo } from "@/components/StoreInfo";
import { CartDock } from "@/components/CartDock";
import { ChatWidget } from "@/components/ChatWidget";
import { AdminPanel } from "@/components/AdminPanel";
import { AuthCallback } from "@/components/AuthCallback";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { OrderTracking } from "@/components/OrderTracking";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Instagram } from "lucide-react";
import { INSTAGRAM_URL } from "@/data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const playDing = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1174.66].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.18;
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.55);
    });
    setTimeout(() => ctx.close(), 1200);
  } catch {}
};

function Main() {
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const scrollTo = useCallback((target) => {
    const offset = window.innerWidth < 1024 ? -118 : -72;
    lenisRef.current?.scrollTo(target, { offset, duration: 1.3 });
  }, []);

  const { user } = useAuth();
  const knownOrderCount = useRef(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    let cancelled = false;
    const checkOrders = async () => {
      try {
        const r = await fetch(`${API}/orders`, { credentials: "include" });
        if (!r.ok) return;
        const list = await r.json();
        if (cancelled) return;
        if (knownOrderCount.current !== null && list.length > knownOrderCount.current) {
          playDing();
          toast.success("Novo pedido recebido!", {
            duration: 10000,
            action: { label: "Ver pedidos", onClick: () => setAdminOpen(true) },
          });
        }
        knownOrderCount.current = list.length;
      } catch {}
    };
    checkOrders();
    const id = setInterval(checkOrders, 20000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user]);

  const addToCart = useCallback((item) => {
    setCart((prev) => [
      ...prev,
      { ...item, uid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
    ]);
    toast.success(`${item.title} adicionado ao pedido`);
  }, []);

  const inc = useCallback(
    (uid) => setCart((prev) => prev.map((i) => (i.uid === uid ? { ...i, qty: i.qty + 1 } : i))),
    []
  );
  const dec = useCallback(
    (uid) =>
      setCart((prev) =>
        prev
          .map((i) => (i.uid === uid ? { ...i, qty: i.qty - 1 } : i))
          .filter((i) => i.qty > 0)
      ),
    []
  );
  const remove = useCallback((uid) => setCart((prev) => prev.filter((i) => i.uid !== uid)), []);
  const clear = useCallback(() => setCart([]), []);

  const count = cart.reduce((acc, i) => acc + i.qty, 0);
  const total = cart.reduce((acc, i) => acc + i.unitPrice * i.qty, 0);

  return (
    <div className="App min-h-screen bg-[#1E053B] text-white antialiased">
      <Header cartCount={count} onCartOpen={() => setCartOpen(true)} onNavigate={scrollTo} onAdminOpen={() => setAdminOpen(true)} />
      <main>
        <Hero onNavigate={scrollTo} />
        <Marquee />
        <section id="monte" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <AISuggester onAdd={addToCart} />
          <CopoBuilder onAdd={addToCart} hasItems={count > 0} />
        </section>
        <section id="roleta" className="mx-auto max-w-7xl scroll-mt-20 border-t border-purple-500/15 px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <Roleta onAdd={addToCart} />
        </section>
        <section id="promos" className="mx-auto max-w-7xl scroll-mt-20 border-t border-purple-500/15 px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <Promos onAdd={addToCart} />
        </section>
        <section id="sorvetes" className="mx-auto max-w-7xl scroll-mt-20 border-t border-purple-500/15 px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <ExtrasSection onAdd={addToCart} />
        </section>
        <section id="loja" className="mx-auto max-w-7xl scroll-mt-20 border-t border-purple-500/15 px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <StoreInfo />
        </section>
      </main>
      <footer className="border-t border-purple-500/15 px-4 py-10 pb-32 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4">
          <p className="font-syne text-lg font-extrabold text-white">
            GIGA<span className="text-[#A855F7]">AÇAÍ</span>
          </p>
          <div className="flex items-center gap-4">
            <a
              data-testid="footer-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram da loja"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-purple-500/30 text-purple-200 transition-colors duration-200 hover:border-pink-400 hover:text-pink-300"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <p className="text-xs text-purple-300/50">
              © 2026 Giga Açaí · Curitiba — Peça pelo site e finalize no WhatsApp.
            </p>
          </div>
        </div>
      </footer>
      <CartDock
        cart={cart}
        total={total}
        count={count}
        open={cartOpen}
        setOpen={setCartOpen}
        onInc={inc}
        onDec={dec}
        onRemove={remove}
        onClear={clear}
        onNavigate={scrollTo}
      />
      <ChatWidget onAdd={addToCart} lifted={count > 0 && !cartOpen} />
      <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  if (location.pathname.startsWith("/pedido/")) {
    return <OrderTracking orderId={location.pathname.split("/pedido/")[1]} />;
  }
  return <Main />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
